import * as THREE from 'three';
import { SceneNode } from '../../core/SceneNode';
import { ThreeGeomFactory } from '../../shapes/ThreeGeomFactory';
import type { BridgePin } from '../../../embed/MapBridge';
import { createMapMarkerTube } from './MapMarkerGeometry';

export interface MapCategoryPinStyle {
    background: string;
    foreground: string;
    icon: 'parking' | 'bus' | 'food' | 'ticket' | 'helipad' | 'info';
}

const CATEGORY_ICON_URLS: Record<MapCategoryPinStyle['icon'], string | null> = {
    parking: '/storage-api/storage/media/119062?v=d9c9047bc9423d633ce77994bf414537ff6f2039e009abe88b925f70d298fc57',
    bus: '/storage-api/storage/media/119063?v=363d035faedaaa3912156203d2fce1bdc8bad40ea598f3284121b6d922e31936',
    food: '/storage-api/storage/media/119064?v=926df38cbf9f2265705d056c0bf2b675114a89d0d5fe0d86f62502a1d2d0d76d',
    ticket: '/storage-api/storage/media/119065?v=6a4814ef188f38e8cc7cae05b3a464ad89a7f93af810ebbaacb937285b06f021',
    helipad: null,
    info: null,
};

/**
 * True for map infrastructure that is better represented by compact wayfinding
 * signage than by a photographic Presenter card. Rich media remains available
 * in the POI detail sheet.
 */
export function isCategoryCubePin(pin: BridgePin): boolean {
    return pin.kind === 'poi' && (pin.category === 'amenity' || pin.category === 'transport');
}

export function categoryPinStackRank(pin: BridgePin): number {
    switch (pin.subcategory) {
        case 'parking': return 0;
        case 'bus_stop': return 1;
        case 'restaurant':
        case 'kiosk': return 2;
        case 'ticket': return 3;
        case 'firstaid': return 4;
        default: return 5;
    }
}

/** Helipad is orientation infrastructure, not a primary attraction. */
export function categoryPinMarkerScale(pin: BridgePin): number {
    return pin.subcategory === 'firstaid' ? 0.46 : 1;
}

export function getCategoryPinStyle(pin: BridgePin): MapCategoryPinStyle {
    switch (pin.subcategory) {
        case 'parking':
            return { background: '#3159B8', foreground: '#FFFFFF', icon: 'parking' };
        case 'bus_stop':
            return { background: '#3553A4', foreground: '#FFFFFF', icon: 'bus' };
        case 'restaurant':
        case 'kiosk':
            return { background: '#F5F1E8', foreground: '#171A21', icon: 'food' };
        case 'ticket':
            return { background: '#D94A3E', foreground: '#FFFFFF', icon: 'ticket' };
        case 'firstaid':
            // ArTrack's `firstaid` record is specifically the helicopter landing
            // site, not a general first-aid station. Use the map-standard H mark.
            return { background: '#D85A50', foreground: '#FFFFFF', icon: 'helipad' };
        default:
            return { background: '#3159B8', foreground: '#FFFFFF', icon: 'info' };
    }
}

function drawIcon(
    context: CanvasRenderingContext2D,
    icon: MapCategoryPinStyle['icon'],
    color: string,
): void {
    context.save();
    context.strokeStyle = color;
    context.fillStyle = color;
    context.lineWidth = 16;
    context.lineCap = 'round';
    context.lineJoin = 'round';

    if (icon === 'parking') {
        context.font = '700 150px Arial, sans-serif';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText('P', 128, 138);
    } else if (icon === 'bus') {
        context.beginPath();
        context.roundRect(64, 48, 128, 142, 20);
        context.stroke();
        context.fillRect(82, 72, 40, 44);
        context.fillRect(134, 72, 40, 44);
        context.beginPath();
        context.arc(91, 193, 15, 0, Math.PI * 2);
        context.arc(165, 193, 15, 0, Math.PI * 2);
        context.fill();
    } else if (icon === 'food') {
        context.beginPath();
        context.moveTo(82, 48); context.lineTo(82, 208);
        context.moveTo(58, 48); context.lineTo(58, 104);
        context.moveTo(82, 48); context.lineTo(82, 104);
        context.moveTo(106, 48); context.lineTo(106, 104);
        context.moveTo(58, 104); context.quadraticCurveTo(82, 128, 106, 104);
        context.moveTo(164, 48); context.lineTo(164, 208);
        context.moveTo(164, 48); context.quadraticCurveTo(211, 82, 164, 132);
        context.stroke();
    } else if (icon === 'ticket') {
        context.beginPath();
        context.moveTo(52, 80); context.lineTo(204, 80);
        context.lineTo(204, 112); context.quadraticCurveTo(172, 128, 204, 144);
        context.lineTo(204, 176); context.lineTo(52, 176);
        context.lineTo(52, 144); context.quadraticCurveTo(84, 128, 52, 112);
        context.closePath(); context.stroke();
        context.setLineDash([12, 12]);
        context.beginPath(); context.moveTo(128, 88); context.lineTo(128, 168); context.stroke();
    } else if (icon === 'helipad') {
        context.font = '800 154px Arial, sans-serif';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText('H', 128, 137);
    } else {
        context.font = '700 140px Georgia, serif';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText('i', 128, 132);
    }
    context.restore();
}

function drawGeneratedIcon(
    context: CanvasRenderingContext2D,
    image: CanvasImageSource,
    foreground: string,
    background: string,
): void {
    context.save();
    context.clearRect(0, 0, 256, 256);
    context.drawImage(image, 0, 0, 256, 256);
    context.globalCompositeOperation = 'source-in';
    context.fillStyle = foreground;
    context.fillRect(0, 0, 256, 256);
    context.globalCompositeOperation = 'destination-over';
    context.fillStyle = background;
    context.fillRect(0, 0, 256, 256);
    context.restore();
}

function createIconTexture(style: MapCategoryPinStyle): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('2D canvas unavailable for map category marker');
    context.fillStyle = style.background;
    context.fillRect(0, 0, canvas.width, canvas.height);
    // Show the built-in symbol immediately; replace it with the generated
    // high-quality mask as soon as Storage has returned it.
    drawIcon(context, style.icon, style.foreground);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    const iconUrl = CATEGORY_ICON_URLS[style.icon];
    if (iconUrl) {
        const image = new Image();
        image.decoding = 'async';
        image.onload = () => {
            drawGeneratedIcon(context, image, style.foreground, style.background);
            texture.needsUpdate = true;
        };
        image.onerror = () => {
            texture.needsUpdate = true;
        };
        image.src = iconUrl;
    } else {
        texture.needsUpdate = true;
    }
    return texture;
}

/**
 * Scene-node wrapper for a single cube in a data-driven map sign stack. Keeping
 * it in the engine hierarchy makes camera bounds, picking and destruction work
 * like normal POI shapes while still allowing a purpose-built Three.js visual.
 */
export class MapCategoryPinNode extends SceneNode {
    private readonly localBounds: THREE.Box3;

    constructor(
        pin: BridgePin,
        cubeSize: number,
        poleHeight: number,
    ) {
        super(`MapCategoryPin_${pin.id}`);
        const group = new THREE.Group();
        group.name = this.name;
        group.userData.sceneNode = this;
        group.userData.bridgePinId = pin.id;

        const style = getCategoryPinStyle(pin);
        const markerScale = categoryPinMarkerScale(pin);
        cubeSize *= markerScale;
        const texture = createIconTexture(style);
        const cubeGeometry = ThreeGeomFactory.createRoundedBoxWithBeveledEdges(
            cubeSize,
            cubeSize,
            cubeSize,
            cubeSize * 0.08,
            cubeSize * 0.025,
            1,
        );
        const cubeMaterial = new THREE.MeshStandardMaterial({
            map: texture,
            roughness: 0.72,
            metalness: 0.02,
        });
        const cube = new THREE.Mesh(cubeGeometry, cubeMaterial);
        cube.name = `CategoryCube_${pin.id}_${pin.subcategory ?? pin.category ?? 'info'}`;
        cube.castShadow = true;
        cube.receiveShadow = true;
        cube.userData.bridgePinId = pin.id;
        group.add(cube);

        if (poleHeight > 0) {
            const pole = createMapMarkerTube(
                -cubeSize * 0.5,
                -cubeSize * 0.5 - poleHeight,
                cubeSize * 0.018,
            );
            pole.name = `CategoryPole_${pin.id}`;
            group.add(pole);
        }

        this._mesh = group;
        this.localBounds = new THREE.Box3(
            new THREE.Vector3(-cubeSize * 0.5, -cubeSize * 0.5 - poleHeight, -cubeSize * 0.5),
            new THREE.Vector3(cubeSize * 0.5, cubeSize * 0.5, cubeSize * 0.5),
        );
    }

    protected calculateBoundingBox(): THREE.Box3 {
        return this.localBounds.clone();
    }

    protected override onDestroy(): void {
        this._mesh?.traverse((object: THREE.Object3D) => {
            const mesh = object as THREE.Mesh;
            mesh.geometry?.dispose?.();
            const materials = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
            for (const material of materials) {
                const map = (material as THREE.MeshStandardMaterial).map;
                map?.dispose();
                material.dispose();
            }
        });
        this._mesh?.removeFromParent();
        this._mesh = null;
        super.onDestroy();
    }
}
